/**
 * Auth Store
 *
 * Zustand store for authentication state
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthUser } from '@/types';

interface AuthStore {
  // State
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;

  // Actions
  setAuth: (user: AuthUser, token: string) => void;
  clearAuth: () => void;
  updateUser: (user: Partial<AuthUser>) => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      // Initial state
      user: null,
      token: null,
      isAuthenticated: false,

      // Set authentication data
      setAuth: (user, token) => {
        // Store in localStorage for API client
        localStorage.setItem('admin_token', token);
        localStorage.setItem('admin_user_id', user.user_id);
        if (user.email) {
          localStorage.setItem('admin_email', user.email);
        }
        if (user.username) {
          localStorage.setItem('admin_username', user.username);
        }

        set({
          user,
          token,
          isAuthenticated: true,
        });
      },

      // Clear authentication data
      clearAuth: () => {
        // Clear localStorage
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user_id');
        localStorage.removeItem('admin_email');
        localStorage.removeItem('admin_username');
        sessionStorage.removeItem('admin_token');

        set({
          user: null,
          token: null,
          isAuthenticated: false,
        });
      },

      // Update user info
      updateUser: (userUpdate) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...userUpdate } : null,
        })),
    }),
    {
      name: 'admin-auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
